import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import InvoiceModal from "./create-invoice-modal";
import { listInvoicesByServiceOrder } from "../../entities/invoice/api/list-invoices-by-service-order";
import { Invoice } from "../../entities/invoice/model/invoice";
import InvoiceListItemComponent from "../../features/invoice/list-invoices/ui/invoice-list-item.component";
import { PaperCard } from "../../shared/ui/paper-card/paper-card.component";
import { currencyMask, formatIntegerToCurrency } from "../../shared/lib/numbers";
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  ListItem,
  ListItemText
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Formik, Form } from "formik";
import CostumerAutocompleteSearchBar from "../../entities/costumer/ui/costumer-autocomplete-search-bar.component";
import dayjs from "dayjs";
import { CreateNewCostumer } from "../../entities/costumer/model/costumer";
import { Car } from "../../entities/car";
import { ServiceOrder, getServiceOrder } from "../../entities/service-order";
import { toast, ToastContainer } from "react-toastify";
import {
  createServiceOrderLine,
  deleteServiceOrderLine,
  getServiceOrderLinesByServiceOrder,
  updateServiceOrderLine
} from "../../entities/service-order-line/api";
import { updateServiceOrderStatus, updateServiceOrder } from "../../entities/service-order/api";
import {
  GetServiceOrderLineTypeId,
  ServiceOrderLine,
  calculateServiceOrderLineCost,
  canBillServiceOrderLine
} from "../../entities/service-order-line/model/service-order-line";
import {
  PERICIA_SERVICE_ORDER_LINE_TYPE,
  OTHERS_SERVICE_ORDER_LINE_TYPE
} from "../../entities/service-order-line/model/service-order-line";
import supabase from "../../shared/database/supabase";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/confirm-modal/confirm-modal.component";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ServiceOrderPdf, { ServiceOrderData } from "../../features/service-orders/generate-pdf/user-service-orders-pdf";
import { useListCostumers } from "../../features/costumer/list-costumers";

const ServiceOrderEditPage = () => {
  const navigate = useNavigate();
  const { serviceOrderId } = useParams();
  const [serviceOrder, setServiceOrder] = useState<ServiceOrder | null>(null);
  const [lines, setLines] = useState<ServiceOrderLine[]>([]);
  const [cars, setCars] = useState<Record<string, Car>>({});
  const [loading, setLoading] = useState(true);
  const [selectedCostumer, setSelectedCostumer] = useState<string>("");
  const [openTypeModal, setOpenTypeModal] = useState(false);
  const [selectedType, setSelectedType] = useState<string>("");
  const [newLineValues, setNewLineValues] = useState({
    workHours: 0,
    costByWorkHour: 0,
    discount: 0
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [lineToDelete, setLineToDelete] = useState<ServiceOrderLine | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    description?: string;
    onConfirm: () => void;
  } | null>(null);
  const [editedLines, setEditedLines] = useState<Record<string, Partial<ServiceOrderLine>>>({});
  const [linesToBill, setLinesToBill] = useState<Record<string, boolean>>({});
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const { data: costumers } = useListCostumers();
  const user = supabase.auth.user();

  // Generate real service order data for PDF
  const generateServiceOrderData = (): ServiceOrderData => {
    if (!serviceOrder) {
      return {} as ServiceOrderData;
    }
    console.log("Generating PDF data for service order:", lines);

    // Get vehicle data from first pericia line with car info
    const getVehicleData = () => {
      const periciaLine = lines.find((line) => line.serviceOrderLineType.name === PERICIA_SERVICE_ORDER_LINE_TYPE && line.car);

      if (periciaLine && periciaLine.car && cars[periciaLine.car.id]) {
        const car = cars[periciaLine.car.id];
        return {
          chassisNumber: car.chassisNumber || "N/A",
          model: `${car.brand || ""} ${car.model || ""}`.trim() || "N/A"
        };
      }

      return {
        chassisNumber: "N/A",
        model: "N/A"
      };
    };

    const vehicleData = getVehicleData();

    // Get customer name from the costumers list
    const selectedCostumerData = costumers?.find((c) => c.id === selectedCostumer);
    const customerName = selectedCostumerData?.name || "N/A";

    // Map service order lines to PDF items
    const serviceOrderItems = lines.map((line) => {
      // Get car data if line has a car
      const carData = line.car && cars[line.car.id] ? cars[line.car.id] : null;

      // Get working hours from edited lines or original line
      const workingHours = editedLines[line.id]?.workHours ?? line.workHours ?? 0;

      return {
        description: line.description || line.serviceOrderLineType.name,
        workingHours: workingHours,
        total: line.cost || 0,
        vehicleType: carData ? `${carData.brand || ""} ${carData.model || ""}`.trim() : undefined,
        licensePlate: carData ? carData.plate : undefined,
        chassisNumber: carData ? carData.chassisNumber : undefined,
        createdAt: line.createdAt ? dayjs(line.createdAt).format("YYYY-MM-DD HH:mm") : "N/A"
      };
    });
    return {
      serviceOrderCode: serviceOrder.id,
      orderNumber: serviceOrder.id,
      referenceNumber: serviceOrder.referenceNumber,
      barcode: "https://fakeimg.pl/150x20/?text=BARCODE",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/9/90/Mercedes-Logo.svg",
      customerTitle: "Cliente",
      customerName: customerName,
      customerAddress: selectedCostumerData?.address || "N/A",
      customerPhone: selectedCostumerData?.phone || "N/A",
      customerEmail: selectedCostumerData?.email || "N/A",
      customerCode: selectedCostumer || "N/A",
      date: dayjs(serviceOrder.createdAt).format("DD.MM.YYYY"),
      licensePlate: "N/A",
      chassisNumber: vehicleData.chassisNumber,
      engineNumber: "N/A",
      kilometers: "N/A",
      carModel: vehicleData.model,
      technician: "", // Empty as requested
      technicianName: "", // Empty as requested
      deliveryDate: serviceOrder.finished ? dayjs(serviceOrder.updatedAt).format("DD.MM.YYYY") : "TBD",
      deliveryTime: serviceOrder.finished ? dayjs(serviceOrder.updatedAt).format("HH:mm") : "TBD",
      serviceDescription: `Ordem de Serviço ${serviceOrder.finished ? "Concluída" : "Aberta"}`,
      items: serviceOrderItems,
      technicianNotes: "",
      signatureCustomer: "",
      signatureTechnician: "",
      companyName: "NUOVAUTO di Keil Roger",
      companyAddress: "Via San Gottardo 22/A, 6532 Arbedo-Castione",
      companyPhone: "+41 76 746 44 12",
      companyEmail: "amministrazione@nuovauto.ch",
      companyVAT: "CHE-162.607.499",
      serviceItems: serviceOrderItems
    };
  };

  const fetchData = async () => {
    setLoading(true);
    if (!serviceOrderId) {
      console.error("Service order Id is not provided.");
      return;
    }
    const { serviceOrder, error: resError } = await getServiceOrder(serviceOrderId);
    if (resError || !serviceOrder) {
      console.error("Error fetching service order:", resError);
      setLoading(false);
      return;
    }
    const { serviceOrderLines, error: resSOLError } = await getServiceOrderLinesByServiceOrder([serviceOrderId]);
    if (resSOLError || !serviceOrderLines) {
      console.error("Error fetching service order lines:", resSOLError);
      setLoading(false);
      return;
    }
    console.log("Fetched service order and lines:", serviceOrder, serviceOrderLines);
    setServiceOrder(serviceOrder);
    setSelectedCostumer(serviceOrder.costumerId);
    setLines(serviceOrderLines);

    const periciaCarLines = serviceOrderLines.filter(
      (line) => line.serviceOrderLineType.name === PERICIA_SERVICE_ORDER_LINE_TYPE && line.car
    );

    const carMap: Record<string, Car> = {};
    periciaCarLines.forEach((line) => {
      const car = line.car;
      if (car && car.id) {
        carMap[car.id] = {
          ...car,
          mileage: null,
          costumerId: "",
          createdAt: new Date(),
          updatedAt: new Date()
        };
      }
    });
    setCars(carMap);
    setLoading(false);
  };

  const fetchInvoices = async () => {
    if (!serviceOrderId) return;

    setLoadingInvoices(true);
    try {
      const response = await listInvoicesByServiceOrder(serviceOrderId);

      const transformedInvoices = (response.invoices || []).map((inv: any) => ({
        id: inv.id,
        costumer: {
          id: inv.costumer.id,
          name: inv.costumer?.name || "Cliente não encontrado",
          address: "",
          fullAddress: "",
          language: "",
          phone: "",
          phone2: "",
          email: "",
          email2: "",
          email3: "",
          priceVersion: 2,
          createdAt: new Date(),
          updatedAt: new Date()
        },
        totalPrice: inv.total_price || 0,
        totalPericias: inv.total_pericias || 0,
        ivaPercentage: inv.iva_percentage || 0,
        invoiceText: inv.invoice_text || "",
        payed: inv.payed || false,
        payedAt: inv.payed_at ? new Date(inv.payed_at) : new Date(),
        payedValue: inv.payed_value || 0,
        startDate: inv.start_date ? new Date(inv.start_date) : new Date(),
        endDate: inv.end_date ? new Date(inv.end_date) : new Date(),
        deleted: inv.deleted || false,
        createdAt: new Date(inv.created_at),
        updatedAt: new Date(inv.updated_at || inv.created_at),
        invoiceCount: inv.invoice_count || 0,
        invoiceCustomCount: inv.invoice_custom_count || 0
      }));

      setInvoices(transformedInvoices);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Erro ao carregar faturas.", { autoClose: 600 });
    } finally {
      setLoadingInvoices(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchInvoices();
  }, [serviceOrderId]);

  if (loading || !serviceOrder)
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <CircularProgress />
      </Box>
    );

  const updateCostumer = (newCostumer: { id: string }) => {
    setSelectedCostumer(newCostumer.id);
  };

  const resetNewLineForm = () => {
    setSelectedType("");
    setNewLineValues({
      workHours: 0,
      costByWorkHour: 0,
      discount: 0
    });
  };

  const handleCreateServiceOrderLine = async (type: string) => {
    if (!user) {
      return toast.error("Usuário não encontrado.", { autoClose: 600 });
    }
    const typeId = GetServiceOrderLineTypeId(type);
    if (typeId instanceof Error) {
      return toast.error(typeId.message, { autoClose: 600 });
    }

    const serviceOrderLine = {
      serviceOrderId: serviceOrder.id,
      createdBy: user.id,
      serviceOrderLineType: { id: typeId, name: "" },
      cost: calculateServiceOrderLineCost(newLineValues.workHours, newLineValues.costByWorkHour),
      discount: newLineValues.discount,
      workHours: newLineValues.workHours,
      costByWorkHour: newLineValues.costByWorkHour
    } as ServiceOrderLine;
    const res = await createServiceOrderLine(serviceOrderLine);

    if (res.error) {
      toast.error("Erro ao criar linha de ordem de serviço.", { autoClose: 600 });
      return;
    }
    toast.success("Linha de ordem de serviço criada com sucesso!", { autoClose: 600 });
    resetNewLineForm();
    const { serviceOrderLines, error: resSOLError } = await getServiceOrderLinesByServiceOrder([serviceOrder.id]);
    if (resSOLError || !serviceOrderLines) {
      console.error("Error fetching service order lines:", resSOLError);
      setLoading(false);
      return;
    }
    setLines(serviceOrderLines);
  };

  const fetchServiceOrderLines = async (serviceOrderId: string) => {
    const { serviceOrderLines, error: resSOLError } = await getServiceOrderLinesByServiceOrder([serviceOrder.id]);
    if (resSOLError || !serviceOrderLines) {
      console.error("Error fetching service order lines:", resSOLError);
      setLoading(false);
      return;
    }
    setLines(serviceOrderLines);
  };

  // Prepare lines array for the modal
  const linesToCreateInvoice = lines
    .filter((line) => linesToBill[line.id])
    .map((line) => ({
      line_id: line.id,
      pericia_id: line.nuovautoClaimId || null,
      car_id: line.car?.id || null,
      date: dayjs(line.createdAt).format("YYYY-MM-DD"),
      cost: line.cost || 0
    }));

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          width: "100%",
          minHeight: "72vh",
          marginTop: 8,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Typography component="h1" variant="h4" mb={2}>
          Editar Ordem de Serviço
        </Typography>
        <Paper
          elevation={3}
          sx={{
            marginTop: 2,
            padding: { xs: 2, sm: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          <Formik
            enableReinitialize
            initialValues={{
              costumerId: selectedCostumer
            }}
            onSubmit={async (values) => {}}
          >
            {({ isSubmitting }) => (
              <Form style={{ width: "100%" }}>
                <Typography variant="h6" gutterBottom>
                  Detalhes da Ordem de Serviço
                </Typography>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12}>
                    <CostumerAutocompleteSearchBar
                      value={CreateNewCostumer({ id: selectedCostumer })}
                      onChange={(c) => updateCostumer(c || CreateNewCostumer({ id: "" }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography>Criado em: {dayjs(serviceOrder.createdAt).format("YYYY-MM-DD HH:mm")}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography>Status:</Typography>
                      <Chip label={serviceOrder.finished ? "Concluída" : "Aberta"} color={serviceOrder.finished ? "default" : "success"} />
                    </Box>
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={2} mt={4} justifyContent="center">
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || serviceOrder.finished} // Add serviceOrder.finished condition
                    onClick={async () => {
                      if (!user) {
                        return toast.error("Usuário não encontrado.", { autoClose: 600 });
                      }
                      setConfirmModal({
                        open: true,
                        title: "Atualizar Ordem de Serviço",
                        description: "Tem certeza que deseja atualizar esta ordem de serviço?",
                        onConfirm: async () => {
                          setConfirmModal(null);
                          const res = await updateServiceOrder({
                            serviceOrderId: serviceOrder.id,
                            costumerId: selectedCostumer
                          });
                          if (res.error) {
                            return toast.error("Erro ao atualizar ordem de serviço.", { autoClose: 600 });
                          }
                          toast.success("Ordem de serviço atualizada com sucesso!", { autoClose: 600 });
                          fetchData();
                        }
                      });
                    }}
                  >
                    Atualizar Ordem de Serviço
                  </Button>
                  {!serviceOrder.finished && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => {
                        if (!user) {
                          return toast.error("Usuário não encontrado.", { autoClose: 600 });
                        }
                        setConfirmModal({
                          open: true,
                          title: "Concluir Ordem de Serviço",
                          description: "Tem certeza que deseja concluir esta ordem de serviço?",
                          onConfirm: async () => {
                            setConfirmModal(null);
                            const res = await updateServiceOrderStatus({
                              serviceOrderId: serviceOrder.id,
                              finishedBy: user.id,
                              finished: true
                            });
                            if (res.error) {
                              return toast.error("Erro ao concluir ordem de serviço.", { autoClose: 600 });
                            }
                            toast.success("Ordem de serviço concluída.", { autoClose: 600 });
                            fetchData();
                          }
                        });
                      }}
                    >
                      Concluir Ordem de Serviço
                    </Button>
                  )}
                  {serviceOrder.finished && (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() => {
                        if (!user) {
                          return toast.error("Usuário não encontrado.", { autoClose: 600 });
                        }
                        setConfirmModal({
                          open: true,
                          title: "Reabrir Ordem de Serviço",
                          description: "Tem certeza que deseja reabrir esta ordem de serviço?",
                          onConfirm: async () => {
                            setConfirmModal(null);
                            const res = await updateServiceOrderStatus({
                              serviceOrderId: serviceOrder.id,
                              finishedBy: user.id,
                              finished: false
                            });
                            if (res.error) {
                              return toast.error("Erro ao reabrir ordem de serviço.", { autoClose: 600 });
                            }
                            toast.success("Ordem de serviço reaberta.", { autoClose: 600 });
                            fetchData();
                          }
                        });
                      }}
                    >
                      Reabrir Ordem de Serviço
                    </Button>
                  )}
                </Stack>
              </Form>
            )}
          </Formik>
        </Paper>
        <Paper
          elevation={3}
          sx={{
            marginTop: 8,
            padding: { xs: 2, sm: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          <Box style={{ width: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Linhas da Ordem de Serviço
            </Typography>
            <Grid container spacing={2}>
              {lines.length === 0 && (
                <Grid item xs={12}>
                  <Typography color="text.secondary">Nenhuma linha de ordem de serviço encontrada.</Typography>
                </Grid>
              )}
              {lines.map((line) => (
                <Grid item xs={12} key={line.id}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        {line.serviceOrderLineType.name}{" "}
                        {line.serviceOrderLineType.name === PERICIA_SERVICE_ORDER_LINE_TYPE &&
                          line.car &&
                          cars[line.car.id] &&
                          ` — Placa: ${cars[line.car.id].plate} `}
                        <Chip label={line.finished ? "Concluída" : "Aberta"} color={line.finished ? "default" : "success"} />
                      </Typography>
                      <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                        <Typography>
                          {line.serviceOrderLineType.name}{" "}
                          {line.serviceOrderLineType.name === PERICIA_SERVICE_ORDER_LINE_TYPE &&
                            line.car &&
                            cars[line.car.id] &&
                            ` — Placa: ${cars[line.car.id].plate} `}
                          <Chip
                            label={line.finished ? "Concluída" : "Aberta"}
                            color={line.finished ? "default" : "success"}
                            sx={{ ml: 1 }}
                          />
                          {line.serviceOrderLineType.name === PERICIA_SERVICE_ORDER_LINE_TYPE && (
                            <>{line.controlled && <Chip label="Controlada" sx={{ ml: 1, bgcolor: "#2fa102ff", color: "#fff" }} />}</>
                          )}
                          {line.billed && <Chip label="Faturada" color="primary" sx={{ ml: 1 }} />}
                        </Typography>
                        {line.finished &&
                          canBillServiceOrderLine(line) && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!linesToBill[line.id]}
                                  onChange={(e) => {
                                    setLinesToBill((prev) => ({ ...prev, [line.id]: e.target.checked }));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  id={`bill-checkbox-${line.id}`}
                                  color="primary"
                                />
                              }
                              label="Faturar"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ ml: 2 }}
                            />
                          )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ bgcolor: "#fafafa", p: 2 }}>
                      <Grid container spacing={1}>
                        {line.serviceOrderLineType.name !== PERICIA_SERVICE_ORDER_LINE_TYPE ? (
                          <>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Valor"
                                size="small"
                                fullWidth
                                type="number"
                                inputProps={{ min: 0 }}
                                disabled={line.finished}
                                value={editedLines[line.id]?.cost ?? line.cost}
                                onChange={(e) =>
                                  setEditedLines((prev) => ({
                                    ...prev,
                                    [line.id]: { ...prev[line.id], cost: Number(e.target.value) }
                                  }))
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Desconto"
                                size="small"
                                fullWidth
                                type="number"
                                inputProps={{ min: 0 }}
                                disabled={line.finished}
                                value={editedLines[line.id]?.discount ?? line.discount}
                                onChange={(e) =>
                                  setEditedLines((prev) => ({
                                    ...prev,
                                    [line.id]: { ...prev[line.id], discount: Number(e.target.value) }
                                  }))
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Horas de trabalho"
                                size="small"
                                fullWidth
                                type="number"
                                inputProps={{ min: 0 }}
                                disabled={line.finished}
                                value={editedLines[line.id]?.workHours ?? line.workHours}
                                onChange={(e) =>
                                  setEditedLines((prev) => ({
                                    ...prev,
                                    [line.id]: {
                                      ...prev[line.id],
                                      workHours: parseFloat(e.target.value) || 0,
                                      cost: calculateServiceOrderLineCost(
                                        Number(e.target.value),
                                        editedLines[line.id]?.costByWorkHour ?? line.costByWorkHour
                                      )
                                    }
                                  }))
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Custo por horas de trabalho"
                                size="small"
                                fullWidth
                                type="number"
                                inputProps={{ min: 0 }}
                                disabled={line.finished}
                                value={editedLines[line.id]?.costByWorkHour ?? line.costByWorkHour}
                                onChange={(e) =>
                                  setEditedLines((prev) => ({
                                    ...prev,
                                    [line.id]: {
                                      ...prev[line.id],
                                      costByWorkHour: Number(e.target.value),
                                      cost: calculateServiceOrderLineCost(
                                        editedLines[line.id]?.workHours ?? line.workHours,
                                        Number(e.target.value)
                                      )
                                    }
                                  }))
                                }
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">CHF</InputAdornment>
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Descrição"
                                size="small"
                                fullWidth
                                disabled={line.finished}
                                value={editedLines[line.id]?.description ?? line.description}
                                onChange={(e) =>
                                  setEditedLines((prev) => ({
                                    ...prev,
                                    [line.id]: { ...prev[line.id], description: e.target.value }
                                  }))
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} alignContent={"center"}>
                              <Typography>Criada em: {dayjs(line.createdAt).format("YYYY-MM-DD HH:mm")}</Typography>
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid item xs={12} sm={6}>
                              <Typography>Valor: CHF {line.cost}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography>Criada em: {dayjs(line.createdAt).format("YYYY-MM-DD HH:mm")}</Typography>
                            </Grid>
                          </>
                        )}
                        {line.serviceOrderLineType.name === PERICIA_SERVICE_ORDER_LINE_TYPE && line.car && cars[line.car.id] && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <Typography>Placa: {cars[line.car.id].plate}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography>Chassis: {cars[line.car.id].chassisNumber}</Typography>
                            </Grid>
                          </>
                        )}
                        {line.serviceOrderLineType.name === PERICIA_SERVICE_ORDER_LINE_TYPE && line.finished && !line.controlled && (
                          <Grid item xs={12}>
                            <Button
                              variant="outlined"
                              color="info"
                              fullWidth
                              disabled={serviceOrder.finished}
                              onClick={async () => {
                                if (!user) {
                                  return toast.error("Usuário não encontrado.", { autoClose: 600 });
                                }
                                setConfirmModal({
                                  open: true,
                                  title: "Controlar Perícia",
                                  description: "Tem certeza que deseja marcar esta perícia como controlada?",
                                  onConfirm: async () => {
                                    setConfirmModal(null);
                                    const res = await updateServiceOrderLine({
                                      serviceOrderLineId: line.id,
                                      controlled: true
                                    });
                                    if (res.error) {
                                      return toast.error("Erro ao controlar a perícia.", { autoClose: 600 });
                                    }
                                    toast.success("Perícia marcada como controlada!", { autoClose: 600 });
                                    fetchData();
                                  }
                                });
                              }}
                            >
                              Controlar
                            </Button>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={6}>
                              {line.serviceOrderLineType.name !== PERICIA_SERVICE_ORDER_LINE_TYPE ? (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  fullWidth
                                  disabled={line.finished || serviceOrder.finished}
                                  onClick={async () => {
                                    if (!user) {
                                      return toast.error("Usuário não encontrado.", { autoClose: 600 });
                                    }
                                    setConfirmModal({
                                      open: true,
                                      title: "Atualizar Linha de Ordem de Serviço",
                                      description: "Tem certeza que deseja atualizar esta linha de ordem de serviço?",
                                      onConfirm: async () => {
                                        setConfirmModal(null);
                                        const res = await updateServiceOrderLine({
                                          serviceOrderLineId: line.id,
                                          cost: editedLines[line.id]?.cost ?? line.cost,
                                          workHours: editedLines[line.id]?.workHours ?? line.workHours,
                                          costByWorkHour: editedLines[line.id]?.costByWorkHour ?? line.costByWorkHour,
                                          discount: editedLines[line.id]?.discount ?? line.discount,
                                          description: editedLines[line.id]?.description ?? line.description
                                        });
                                        if (res.error) {
                                          return toast.error("Erro ao atualizar linha de ordem de serviço.", { autoClose: 600 });
                                        }
                                        toast.success("Linha de ordem de serviço atualizada com sucesso!", { autoClose: 600 });
                                        fetchData();
                                      }
                                    });
                                  }}
                                >
                                  Atualizar Linha
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  fullWidth
                                  disabled={line.nuovautoClaimId ? false : serviceOrder.finished}
                                  onClick={() => {
                                    line.nuovautoClaimId && line.nuovautoClaimId !== ""
                                      ? navigate("/pericias/" + line.nuovautoClaimId)
                                      : navigate("/pericias/add", {
                                          state: {
                                            costumer: selectedCostumer,
                                            serviceOrderLineId: line.id
                                          }
                                        });
                                  }}
                                >
                                  {line.nuovautoClaimId ? "Ir para Perícia" : "Criar Perícia"}
                                </Button>
                              )}
                            </Grid>
                            <Grid item xs={6}>
                              <Button
                                variant="outlined"
                                color="error"
                                disabled={serviceOrder.finished}
                                fullWidth
                                onClick={() => {
                                  setLineToDelete(line);
                                  setDeleteModalOpen(true);
                                }}
                              >
                                Deletar linha de ordem de serviço
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                        {line.serviceOrderLineType.name !== PERICIA_SERVICE_ORDER_LINE_TYPE && (
                          <>
                            <Grid item xs={12}>
                              <Button
                                variant={line.finished ? "outlined" : "contained"}
                                color={line.finished ? "warning" : "success"}
                                fullWidth
                                sx={{ mt: 1 }}
                                onClick={() => {
                                  if (!user) {
                                    return toast.error("Usuário não encontrado.", { autoClose: 600 });
                                  }
                                  setConfirmModal({
                                    open: true,
                                    title: line.finished ? "Reabrir Linha de Ordem de Serviço" : "Concluir Linha de Ordem de Serviço",
                                    description: line.finished
                                      ? "Tem certeza que deseja reabrir esta linha de ordem de serviço?"
                                      : "Tem certeza que deseja concluir esta linha de ordem de serviço?",
                                    onConfirm: async () => {
                                      setConfirmModal(null);
                                      const finished = !line.finished;
                                      const res = await updateServiceOrderLine({
                                        serviceOrderLineId: line.id,
                                        cost: editedLines[line.id]?.cost ?? line.cost,
                                        workHours: editedLines[line.id]?.workHours ?? line.workHours,
                                        costByWorkHour: editedLines[line.id]?.costByWorkHour ?? line.costByWorkHour,
                                        discount: editedLines[line.id]?.discount ?? line.discount,
                                        description: editedLines[line.id]?.description ?? line.description,
                                        finished: finished,
                                        finishedBy: finished ? user.id : null
                                      });
                                      if (res.error) {
                                        return toast.error(
                                          line.finished
                                            ? "Erro ao reabrir linha de ordem de serviço."
                                            : "Erro ao concluir linha de ordem de serviço.",
                                          { autoClose: 600 }
                                        );
                                      }
                                      toast.success(
                                        line.finished
                                          ? "Linha de ordem de serviço reaberta com sucesso!"
                                          : "Linha de ordem de serviço concluída com sucesso!",
                                        { autoClose: 600 }
                                      );
                                      fetchData();
                                    }
                                  });
                                }}
                              >
                                {line.finished ? "Reabrir Linha" : "Concluir Linha"}
                              </Button>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Grid container spacing={2} sx={{ mt: 1 }} justifyContent="center">
                  <Grid item xs={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setOpenTypeModal(true)}
                      disabled={serviceOrder.finished}
                      fullWidth
                    >
                      Adicionar linha de ordem de serviço
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <PDFDownloadLink
                      document={<ServiceOrderPdf data={generateServiceOrderData()} />}
                      fileName={`ordem-servico-${serviceOrder?.id || "documento"}.pdf`}
                    >
                      {({ loading }) => (
                        <Button variant="contained" color="primary" disabled={loading} fullWidth>
                          {loading ? "Preparando PDF..." : "Baixar PDF"}
                        </Button>
                      )}
                    </PDFDownloadLink>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Paper>
        <Grid item xs={12} sx={{ mt: 4, textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            disabled={linesToCreateInvoice.length === 0 || serviceOrder.finished}
            onClick={() => setInvoiceModalOpen(true)}
            fullWidth
          >
            Gerar Fatura
          </Button>
          <InvoiceModal
            open={invoiceModalOpen}
            onClose={() => setInvoiceModalOpen(false)}
            costumerId={selectedCostumer}
            lines={linesToCreateInvoice}
            onSuccess={() => {
              setInvoiceModalOpen(false);
              setLinesToBill({});
              fetchData();
              fetchInvoices();
            }}
          />
        </Grid>
        <PaperCard title={`Faturas da Ordem de Serviço`}>
          <Box style={{ width: "100%" }}>
            {loadingInvoices ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={30} />
              </Box>
            ) : invoices.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={2}>
                Nenhuma fatura encontrada para esta ordem de serviço.
              </Typography>
            ) : (
              <>
                <Typography variant="body2">
                  Total sem IVA: {currencyMask(formatIntegerToCurrency(invoices.reduce((sum, inv) => sum + (inv.totalPrice || 0), 0)))}
                </Typography>
                <ListItem
                  divider
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    textAlign: "center",
                    marginTop: "2rem"
                  }}
                >
                  {["FATURA", "CLIENTE", "DATA", "VALOR", "TOTAL C/ IVA", "STATUS"].map((item, idx) => (
                    <ListItemText id={`invoice-list-header-${item}`} primary={item} sx={{ flex: "1", fontWeight: "bold" }} key={item} />
                  ))}
                </ListItem>
                {invoices.map((invoice) => (
                  <InvoiceListItemComponent invoice={invoice} key={invoice.id} />
                ))}
              </>
            )}
          </Box>
        </PaperCard>

        <Dialog
          open={openTypeModal}
          onClose={() => {
            setOpenTypeModal(false);
            resetNewLineForm();
          }}
        >
          <DialogTitle>Selecione o tipo de linha</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Button
                variant={selectedType === PERICIA_SERVICE_ORDER_LINE_TYPE ? "contained" : "outlined"}
                onClick={() => setSelectedType(PERICIA_SERVICE_ORDER_LINE_TYPE)}
              >
                {PERICIA_SERVICE_ORDER_LINE_TYPE}
              </Button>
              {/* <Button
                variant={selectedType === CAR_RENTAL_SERVICE_ORDER_LINE_TYPE ? "contained" : "outlined"}
                onClick={() => setSelectedType(CAR_RENTAL_SERVICE_ORDER_LINE_TYPE)}
              >
                {CAR_RENTAL_SERVICE_ORDER_LINE_TYPE}
              </Button> */}
              <Button
                variant={selectedType === OTHERS_SERVICE_ORDER_LINE_TYPE ? "contained" : "outlined"}
                onClick={() => setSelectedType(OTHERS_SERVICE_ORDER_LINE_TYPE)}
              >
                {OTHERS_SERVICE_ORDER_LINE_TYPE}
              </Button>
              <TextField
                label="Horas de trabalho"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={newLineValues.workHours}
                onChange={(e) =>
                  setNewLineValues((prev) => ({
                    ...prev,
                    workHours: parseFloat(e.target.value) || 0
                  }))
                }
              />
              <TextField
                label="Custo por horas de trabalho"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={newLineValues.costByWorkHour}
                onChange={(e) =>
                  setNewLineValues((prev) => ({
                    ...prev,
                    costByWorkHour: parseFloat(e.target.value) || 0
                  }))
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">CHF</InputAdornment>
                }}
              />
              <TextField
                label="Desconto"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={newLineValues.discount}
                onChange={(e) =>
                  setNewLineValues((prev) => ({
                    ...prev,
                    discount: parseFloat(e.target.value) || 0
                  }))
                }
              />
              <TextField
                label="Valor"
                size="small"
                fullWidth
                disabled
                value={calculateServiceOrderLineCost(newLineValues.workHours, newLineValues.costByWorkHour)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">CHF</InputAdornment>
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenTypeModal(false);
                resetNewLineForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (!selectedType) {
                  toast.error("Selecione um tipo de linha.", { autoClose: 600 });
                  return;
                }
                setOpenTypeModal(false);
                await handleCreateServiceOrderLine(selectedType);
              }}
              variant="contained"
              disabled={!selectedType}
            >
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
          <DialogTitle>Deletar linha de ordem de serviço</DialogTitle>
          <DialogContent>
            <Typography>Tem certeza que deseja deletar esta linha de ordem de serviço?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
            <Button
              onClick={async () => {
                if (lineToDelete && lineToDelete.id) {
                  const res = await deleteServiceOrderLine(lineToDelete.id);
                  if (res.error) {
                    toast.error("Erro ao deletar linha de ordem de serviço.", { autoClose: 600 });
                  } else {
                    toast.success("Linha de ordem de serviço deletada com sucesso!", { autoClose: 600 });
                    fetchServiceOrderLines(serviceOrder.id);
                  }
                }
                setDeleteModalOpen(false);
              }}
              variant="contained"
              color="error"
            >
              Deletar
            </Button>
          </DialogActions>
        </Dialog>
        {confirmModal && (
          <ConfirmDialog
            open={confirmModal.open}
            title={confirmModal.title}
            description={confirmModal.description}
            onConfirm={confirmModal.onConfirm}
            onCancel={() => setConfirmModal(null)}
          />
        )}
      </Box>
      <ToastContainer />
    </Container>
  );
};

export default ServiceOrderEditPage;
